import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useReopenTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type UseTicketReopenPopupT = {
  isReopeningTicket: boolean;
  handleReopenTicket: (ticketIds: string[], onClose: NoopT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketReopenPopup = (): UseTicketReopenPopupT => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const reopenTicketMutation = useReopenTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );

  const handleReopenTicket = useCallback(
    (ticketIds: string[], onClose: NoopT) => () => {
      reopenTicketMutation.mutate(
        { ticketIds },
        {
          onSuccess: () => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'Ticket successfully reopened',
                snackBarSeverity: 'success',
              })
            );
            onClose();
          },
          onError: error => {
            const errorMsg =
              getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                snackBarSeverity: 'error',
              })
            );
            onClose();
          },
        }
      );
    },
    [reopenTicketMutation, dispatch]
  );

  return {
    isReopeningTicket: reopenTicketMutation.isLoading,
    handleReopenTicket,
    handleClosePopup,
  };
};

export default useTicketReopenPopup;
